'use client';

import type { ReactNode } from 'react';
import { cn } from '@/libs/utils';

interface StatusTileProps {
  /** Background variant — 'info' (lavender) for pending states, 'success' (light-green) for qualified, 'warning' (linter-yellow) for at-risk */
  variant: 'info' | 'success' | 'warning';
  /** Icon rendered inside the leading chip */
  icon: ReactNode;
  /** Leading-chip background color — defaults derived from variant */
  chipClassName?: string;
  title: string;
  body: string;
}

const VARIANT_BG: Record<StatusTileProps['variant'], string> = {
  info: 'bg-[#EEF3FE]',
  success: 'bg-light-green',
  warning: 'bg-linter-yellow',
};

const VARIANT_CHIP: Record<StatusTileProps['variant'], string> = {
  info: 'bg-motitoBlue',
  success: 'bg-motito-green',
  warning: 'bg-[#0C0C0C]',
};

/**
 * Early Pickup status tile used across drawer states. Consistent layout,
 * variant-driven background and chip color.
 */
export function StatusTile({
  variant,
  icon,
  chipClassName,
  title,
  body,
}: StatusTileProps) {
  return (
    <div className={cn('flex flex-col gap-2.5 rounded-xl p-4', VARIANT_BG[variant])}>
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-lg',
            chipClassName ?? VARIANT_CHIP[variant]
          )}
          aria-hidden="true"
        >
          {icon}
        </div>
        <p className="text-[15px] font-extrabold leading-[1.2] text-[#0C0C0C]">
          {title}
        </p>
      </div>
      <p className="text-[12px] font-medium leading-[1.4] text-[#0C0C0C]">
        {body}
      </p>
    </div>
  );
}

export default StatusTile;
